import React, { useContext, useEffect, useState } from "react";
import {
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@material-ui/core";
import { ExpandMore } from "@material-ui/icons";
import { get, isEmpty, omit, toLower } from "lodash";
import { MediaRequestContext } from "../../context/MediaRequestContext";
import {
  ASSET_ROW_KEYS,
  ASSET_TABLE_COLUMNS,
  PAGE_ERROR,
  CASE_TYPE_MAPPING,
  MEDIA_SEARCH,
  ASSET_TYPE,
  MEDIA_CELL_WIDTH_KEYS,
} from "../../constants/MediaSearch";
import { PageErrorContent } from "../page-error/PageError";
import { ReactComponent as Info } from "../../assets/icons/information.svg";
import "./table.scss";

const MediaRequestEpisodeAccordions = ({ asset, assetIndex }) => {
  const {
    assetData,
    odsEditsData,
    _setOdsEditsData,
    isOdsEditsDataLoading,
    odsEditsFetchError,
    fetchEditsFromODS,
    validAssets,
    _setValidAssets,
    recordType,
    episodeData,
  } = useContext(MediaRequestContext);
  const [showCases, setShowCases] = useState([]);

  const brandNamesList = sessionStorage.getItem(MEDIA_SEARCH.storageKey)
    ? JSON.parse(sessionStorage.getItem(MEDIA_SEARCH.storageKey))
    : null;

  useEffect(() => {
    setShowCases([]);
  }, [assetData, episodeData]);

  useEffect(() => {
    const selectedCases = [...showCases];
    if (recordType === ASSET_TYPE.SERIES && episodeData) {
      Object.keys(episodeData).forEach((key) => {
        for (const { asset } of episodeData[key]) {
          if (odsEditsData[asset.ids.guid]) {
            selectedCases.push(asset.ids.guid);
          }
        }
      });
    } else if (typeof assetData !== "string" && assetData.length) {
      assetData.forEach(({ asset }) => {
        if (odsEditsData[asset.ids.guid]) {
          selectedCases.push(asset.ids.guid);
        }
      });
    }
    setShowCases(selectedCases);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getGuidFromIds = (cases) => get(cases, "ids.guid");

  const handleSelectedEdits = (e, guid, selectedManifestation) => {
    let idsToValidate = { ...validAssets };
    const selectedEdits = odsEditsData[guid];
    for (const { node } of selectedEdits) {
      for (const relationship of node?.relationships) {
        for (const manifestationObj of relationship.relatedEntityManifestations) {
          const manifestationId = manifestationObj.id;
          const selectedManifestationId = selectedManifestation.id;
          if (manifestationId === selectedManifestationId) {
            manifestationObj["isChecked"] = e.target.checked;
            if (e.target.checked) {
              const updatedData = {
                ...relationship,
                manifestation: manifestationObj,
                seriesTitle: node.seriesTitle,
                seasonTitle: node.seasonTitle,
                displayTitle: node.displayTitle,
                episodeNumber: node.episodeNumber,
              };
              idsToValidate = {
                ...idsToValidate,
                [manifestationId]: updatedData,
              };
            } else if (idsToValidate.hasOwnProperty(manifestationId)) {
              idsToValidate = omit(idsToValidate, manifestationId);
            }
          }
        }
      }
    }
    _setValidAssets(idsToValidate);
    _setOdsEditsData({
      ...odsEditsData,
      [guid]: selectedEdits,
    });
  };

  const handleSelectAllEdits = (e, asset) => {
    const guid = getGuidFromIds(asset);
    let idsToValidate = { ...validAssets };
    if (odsEditsData && guid in odsEditsData) {
      const selectedEdits = odsEditsData[guid];
      for (const { node } of selectedEdits) {
        for (const relationship of node?.relationships) {
          for (const manifestationObj of relationship.relatedEntityManifestations) {
            const manifestationId = manifestationObj.id;
            manifestationObj["isChecked"] = e.target.checked;
            if (e.target.checked) {
              const updatedData = {
                ...relationship,
                manifestation: manifestationObj,
                seriesTitle: node.seriesTitle,
                seasonTitle: node.seasonTitle,
                displayTitle: node.displayTitle,
                episodeNumber: node.episodeNumber,
              };
              idsToValidate = {
                ...idsToValidate,
                [manifestationId]: updatedData,
              };
            } else if (idsToValidate.hasOwnProperty(manifestationId)) {
              idsToValidate = omit(idsToValidate, manifestationId);
            }
          }
        }
      }
      _setValidAssets(idsToValidate);
      _setOdsEditsData({
        ...odsEditsData,
        [guid]: selectedEdits,
      });
    }
  };

  const findShowCase = (id) => showCases.includes(id);

  const handleShowCases = (id) => {
    if (!findShowCase(id)) {
      setShowCases([...showCases, id]);
    } else {
      const hideShowCase = showCases.filter((item) => item !== id);
      setShowCases(hideShowCase);
    }
  };

  const handleFetchEdits = (relatedRecords, guid) => {
    if (!odsEditsData[guid]) {
      fetchEditsFromODS(relatedRecords, guid);
    }
  };

  const secondsToSMPTE = (seconds, framerate) => {
    let finalValue = null;
    if (seconds && framerate) {
      let f = Math.floor((seconds % 1) * framerate);
      let s = Math.floor(seconds);
      let m = Math.floor(s / 60);
      let h = Math.floor(m / 60);
      m = m % 60;
      s = s % 60;

      let timecode = {
        h: h,
        m: m,
        s: s,
        f: f,
        hStr: h.toString(),
        mStr: m.toString(),
        sStr: s.toString(),
        fStr: f.toString(),
      };

      if (timecode.h < 10) {
        timecode.hStr = "0" + timecode.h;
      }
      if (timecode.m < 10) {
        timecode.mStr = "0" + timecode.m;
      }
      if (timecode.s < 10) {
        timecode.sStr = "0" + timecode.s;
      }
      if (timecode.f < 10) {
        timecode.fStr = "0" + timecode.f;
      }

      finalValue =
        timecode.hStr +
        ":" +
        timecode.mStr +
        ":" +
        timecode.sStr +
        ":" +
        timecode.fStr;
    }
    return finalValue;
  };

  const renderEditsTableCells = (manifestationObj) => {
    return ASSET_ROW_KEYS.map((key, index) => {
      let value;
      if (key === "controllingNetwork") {
        value = brandNamesList[get(manifestationObj, key, "")];
        if (!value) {
          value = get(manifestationObj, key, "");
        }
      } else if (key === "compositionInterval.durationSeconds") {
        value = secondsToSMPTE(get(manifestationObj, key, ""), 24);
      } else if (key === "format" || key === "caseType") {
        value = get(manifestationObj, key, "");
      } else if (key === "components") {
        const videoComponent = manifestationObj.components.find(component => 
          component.componentType === "urn:warnermedia:manifestation:component-type:video");
        value = get(videoComponent, "frameRateCalculated", "");
      }
      return (
        <TableCell
          className="media-cell-font"
          key={`${value}-${index}`}
          title={value}
        >
          <p
            className="media-cell-width"
            style={{
              width: MEDIA_CELL_WIDTH_KEYS.includes(key) ? 90 : 160,
            }}
          >
            {value}
            {key === "caseType" && value ? (
              <Tooltip
                title={
                  CASE_TYPE_MAPPING[toLower(value)] ||
                  "No description available"
                }
                interactive
                placement="top"
                arrow
                classes={{ tooltip: "media-request-table-tooltip" }}
              >
                <Info className="media-request-table-info-icon" />
              </Tooltip>
            ) : null}
          </p>
        </TableCell>
      );
    });
  };

  const renderEditsTableRow = (guid) =>
    odsEditsData[guid].map(({ node }, index) => {
      return node?.relationships?.map((relationship) => {
        return relationship?.relatedEntityManifestations?.map(
          (manifestationObj) => {
            const manifestationId = manifestationObj.id;
            return (
              <TableRow
                key={`media-search-${manifestationId}-${index}`}
                className="media-request-row"
              >
                {renderEditsTableCells(manifestationObj)}
                <TableCell key={`checkbox-${index}`}>
                  <Checkbox
                    className="media-row-checkbox"
                    onChange={(e) =>
                      handleSelectedEdits(e, guid, manifestationObj)
                    }
                    checked={manifestationObj.isChecked || false}
                  />
                </TableCell>
              </TableRow>
            );
          }
        );
      });
    });

  const renderSpinner = () => (
    <TableRow key="media-component">
      <TableCell colSpan={12} className="media-no-results">
        <CircularProgress size={35} />
      </TableCell>
    </TableRow>
  );

  const renderErrorPage = (callback) => (
    <TableRow key="media-component">
      <TableCell colSpan={12} className="media-no-results">
        {errorPage(callback)}
      </TableCell>
    </TableRow>
  );

  const errorPage = (callback) => (
    <div className="media-search-table-header media-error-container">
      <PageErrorContent
        title={PAGE_ERROR.title}
        message={PAGE_ERROR.message}
        reloadBtn={true}
        onReloadClick={callback}
      />
    </div>
  );

  const noResultsMessage = (message) => (
    <p className="media-no-result-paragraph">
      <Info className="media-info-icon" /> {message}.
    </p>
  );

  const getAssetTitle = (asset) => {
    const title = get(asset, "titles.displayTitle[0].value", "");
    const seasonTitle = get(asset, "titles.seasonTitle[0].value", "");
    const episode = get(asset, "inheritedFields.episodeSequence", "");
    const entityType = get(asset, "entityType", "");
    const assetTitle = episode && episode !== "0" ? `${title} (Episode ${episode})` : title;
    return recordType === ASSET_TYPE.ID && entityType === "episode" ? `${seasonTitle} - ${assetTitle}` : assetTitle;
  };

  const isAllEditsSelected = (asset) => {
    return odsEditsData[getGuidFromIds(asset)].every(({ node }) =>
      node?.relationships.every((relationship) =>
        relationship?.relatedEntityManifestations?.every(
          (maniObj) => maniObj.isChecked
        )
      )
    );
  };

  return (
    <Accordion
      classes={{
        expanded: "media-accordion",
      }}
      className="media-assets-accordion"
      key={`accordion-${assetIndex}`}
      expanded={findShowCase(asset.ids.guid)}
      onChange={() => {
        handleShowCases(asset.ids.guid);
        handleFetchEdits(asset.relatedRecords, `${asset.ids.guid}`);
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        {getAssetTitle(asset)}
      </AccordionSummary>
      <AccordionDetails>
        <Table>
          <TableHead className="media-search-table-header">
            <TableRow>
              {ASSET_TABLE_COLUMNS.map((item, index) => (
                <TableCell className="media-cell" key={`${item}-${index}`}>
                  {item}
                </TableCell>
              ))}
              <TableCell key={"media-search-id"}>
                <Checkbox
                  className="media-cell media-select-all"
                  onChange={(e) => handleSelectAllEdits(e, asset)}
                  checked={
                    !isEmpty(odsEditsData) &&
                    odsEditsData[getGuidFromIds(asset)] &&
                    isAllEditsSelected(asset)
                  }
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isOdsEditsDataLoading[asset.ids.guid] &&
              !odsEditsData[asset.ids.guid] &&
              renderSpinner()}
            {!isOdsEditsDataLoading[asset.ids.guid] &&
              !odsEditsData[asset.ids.guid] &&
              odsEditsFetchError[asset.ids.guid] &&
              renderErrorPage(() =>
                fetchEditsFromODS(asset.relatedRecords, asset.ids.guid)
              )}
            {!isOdsEditsDataLoading[asset.ids.guid] &&
              !odsEditsFetchError[asset.ids.guid] &&
              !odsEditsData[asset.ids.guid] && (
                <TableRow>
                  <TableCell colSpan={12}>
                    {noResultsMessage(`No cases found`)}
                  </TableCell>
                </TableRow>
              )}
            {!isOdsEditsDataLoading[asset.ids.guid] &&
              !odsEditsFetchError[asset.ids.guid] &&
              odsEditsData[asset.ids.guid] &&
              renderEditsTableRow(asset.ids.guid)}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
};

export default MediaRequestEpisodeAccordions;
